import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { buildTable, buildPlayerTable, progressPct, teamAvgOverall } from "@/lib/tournamentStats";
import { positionColor, positionAbbrev } from "@/lib/playerConstants";
import { resolveTeamColor } from "@/lib/teamColors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  ArrowLeft, Trophy, Plus, Minus, Check, Copy, RotateCcw, Pencil, Star, Users,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
  offen: "bg-zinc-800 text-zinc-300 border-zinc-700",
  laufend: "bg-amber-400/10 text-amber-400 border-amber-400/40",
  beendet: "bg-brand-lighter/10 text-brand-lighter border-brand-lighter/40",
};
const STATUS_LABEL = { offen: "Offen", laufend: "Laufend", beendet: "Beendet" };

export default function TournamentDetail({ readOnly = false }) {
  const { id, token } = useParams();
  const navigate = useNavigate();
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeGame, setActiveGame] = useState(null); // game object
  const [homeGoals, setHomeGoals] = useState(0);
  const [awayGoals, setAwayGoals] = useState(0);
  const [teamModal, setTeamModal] = useState(null); // team object
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [editingTeamIdx, setEditingTeamIdx] = useState(null);
  const [teamNameInput, setTeamNameInput] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const url = readOnly ? `/tournaments/public/${token}` : `/tournaments/${id}`;
      const { data } = await api.get(url);
      setT(data);
      setNameInput(data.name);
    } catch {
      toast.error("Turnier konnte nicht geladen werden");
      navigate(readOnly ? "/login" : "/turniere");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, token]);

  const openGame = (g) => {
    setActiveGame(g);
    setHomeGoals(g.home_goals ?? 0);
    setAwayGoals(g.away_goals ?? 0);
  };

  const saveResult = async () => {
    if (!activeGame) return;
    try {
      const { data } = await api.patch(`/tournaments/${id}/games/${activeGame.id}`, {
        home_goals: Number(homeGoals),
        away_goals: Number(awayGoals),
      });
      setT(data);
      setActiveGame(null);
      toast.success("Ergebnis gespeichert");
    } catch {
      toast.error("Speichern fehlgeschlagen");
    }
  };

  const resetResult = async () => {
    if (!activeGame) return;
    try {
      const { data } = await api.patch(`/tournaments/${id}/games/${activeGame.id}`, { reset: true });
      setT(data);
      setActiveGame(null);
      toast.success("Ergebnis zurückgesetzt");
    } catch {
      toast.error("Zurücksetzen fehlgeschlagen");
    }
  };

  const setLive = async (g) => {
    try {
      const { data } = await api.patch(`/tournaments/${id}/games/${g.id}`, { status: "laufend" });
      setT(data);
    } catch {
      toast.error("Status konnte nicht aktualisiert werden");
    }
  };

  const saveTeamName = async (idx) => {
    const val = teamNameInput.trim();
    if (!val || val === t.teams[idx]?.name) {
      setEditingTeamIdx(null);
      return;
    }
    try {
      const { data } = await api.patch(`/tournaments/${id}/teams/${idx}`, { name: val });
      setT(data);
      setEditingTeamIdx(null);
      toast.success("Team umbenannt");
    } catch {
      toast.error("Team konnte nicht umbenannt werden");
    }
  };

  const saveName = async () => {
    if (!nameInput.trim() || nameInput === t.name) {
      setEditingName(false);
      setNameInput(t.name);
      return;
    }
    try {
      const { data } = await api.patch(`/tournaments/${id}`, { name: nameInput.trim() });
      setT(data);
      setEditingName(false);
      toast.success("Name aktualisiert");
    } catch {
      toast.error("Name konnte nicht gespeichert werden");
    }
  };

  const copyLink = async () => {
    const url = `${window.location.origin}/t/${t.share_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Turnierlink kopiert");
    } catch {
      toast.error("Kopieren nicht möglich");
    }
  };

  const isChanging = t?.mode === "changing";
  const table = useMemo(() => (t ? buildTable(t.teams, t.games) : []), [t]);
  const playerTable = useMemo(() => (t && t.mode === "changing" ? buildPlayerTable(t) : []), [t]);
  const pct = useMemo(() => (t ? progressPct(t.games) : 0), [t]);
  const finished = useMemo(() => (t ? t.games.filter((g) => g.status === "beendet").length : 0), [t]);
  const isComplete = t && t.games.length > 0 && finished === t.games.length;
  const winner = isComplete ? (isChanging ? playerTable[0] : table.length > 0 ? table[0] : null) : null;

  const [rosterView, setRosterView] = useState(null); // {round, slotIdx}
  const [drawing, setDrawing] = useState(null); // round being drawn

  const generateRound = async (r) => {
    const nSlots = t.teams.length;
    const pool = t.player_pool || [];
    if (pool.length < nSlots) { toast.error("Zu wenige Spieler im Pool"); return; }
    setDrawing(r);
    setTimeout(async () => {
      try {
        const crit = t.balance_criterion || "overall";
        const val = (p) => Number(p?.[crit] ?? p?.overall ?? 0);
        // Spieler für JEDEN Spieltag neu verteilen: zufällig mischen, dann jeweils dem
        // Team mit der aktuell niedrigsten Summe zuweisen (ausgewogen + variiert je Spieltag).
        const shuffled = [...pool].sort(() => Math.random() - 0.5);
        const maxSize = Math.ceil(pool.length / nSlots);
        const buckets = Array.from({ length: nSlots }, () => ({ players: [], sum: 0 }));
        for (const p of shuffled) {
          const eligible = buckets.filter((b) => b.players.length < maxSize);
          eligible.sort((a, b) => a.sum - b.sum || Math.random() - 0.5);
          const target = eligible[0];
          target.players.push(p);
          target.sum += val(p);
        }
        const rosters = buckets.map((b) => b.players.map((p) => ({
          id: p.id, name: p.name, position: p.position,
          jahrgang: p.jahrgang, overall: p.overall, talent: p.talent,
        })));
        const { data } = await api.put(`/tournaments/${id}/rounds/${r}/rosters`, { rosters });
        setT(data);
        toast.success(`Spieltag ${r} ausgelost`);
      } catch (e) {
        toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Auslosen fehlgeschlagen");
      } finally {
        setDrawing(null);
      }
    }, 30);
  };

  if (loading) return <div className="text-center py-20 text-zinc-500 font-display uppercase tracking-wider">Lädt…</div>;
  if (!t) return null;

  // Group games by round for display
  const gamesByRound = {};
  for (const g of t.games) {
    if (!gamesByRound[g.round]) gamesByRound[g.round] = [];
    gamesByRound[g.round].push(g);
  }
  const rounds = Object.keys(gamesByRound).map(Number).sort((a, b) => a - b);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {!readOnly && (
        <button
          onClick={() => navigate("/turniere")}
          className="text-xs uppercase tracking-wider text-zinc-500 hover:text-brand-lighter transition flex items-center gap-1 mb-6"
          data-testid="td-back-btn"
        >
          <ArrowLeft className="h-3 w-3" /> Alle Turniere
        </button>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider text-brand-lighter mb-2">
            Turnier · {t.mode === "hin_rueck" ? "Hin & Rück" : t.mode === "changing" ? "Changing Teams" : "Hinrunde"}
          </div>
          {editingName && !readOnly ? (
            <Input
              autoFocus
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onBlur={saveName}
              onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") { setEditingName(false); setNameInput(t.name); } }}
              className="font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight bg-transparent border-0 border-b border-brand-lighter focus-visible:ring-0 px-0 h-auto py-1"
              data-testid="td-name-input"
            />
          ) : (
            <h1
              className={
                "font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight inline-flex items-center gap-2 " +
                (readOnly ? "" : "cursor-pointer hover:text-brand-lighter")
              }
              onClick={() => !readOnly && setEditingName(true)}
              data-testid="td-name"
            >
              {t.name}
              {!readOnly && <Pencil className="h-4 w-4 text-zinc-500" />}
            </h1>
          )}
        </div>
        {!readOnly && (
          <Button
            onClick={copyLink}
            variant="outline"
            className="bg-transparent border-zinc-700 text-zinc-100 hover:text-brand-lighter hover:bg-zinc-900 uppercase tracking-wider font-semibold rounded-sm h-11"
            data-testid="td-copy-link-btn"
          >
            <Copy className="h-4 w-4 mr-2" /> Link kopieren
          </Button>
        )}
      </div>

      {/* Dashboard cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3 mb-6">
        <DashStat label="Teams" value={t.teams.length} testId="stat-teams" />
        <DashStat label="Spiele" value={t.games.length} testId="stat-games" />
        <DashStat label="Gespielt" value={finished} testId="stat-finished" />
        <DashStat label="Offen" value={t.games.length - finished} testId="stat-open" />
        <div className="col-span-2 sm:col-span-4 lg:col-span-1 border border-zinc-800 bg-zinc-900/50 p-3">
          <div className="text-[9px] uppercase tracking-wider text-zinc-500">Fortschritt</div>
          <div className="font-display text-xl font-bold text-brand-lighter tabular-nums" data-testid="stat-progress">{pct}%</div>
          <div className="h-1 bg-zinc-800 mt-1.5 overflow-hidden">
            <div className="h-full bg-brand-lighter transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>

      {winner && (
        <div className="border border-amber-400/40 bg-amber-400/5 p-5 mb-6 flex items-center gap-4" data-testid="td-winner-banner">
          <Trophy className="h-8 w-8 text-amber-400 shrink-0" />
          <div>
            <div className="text-[10px] uppercase tracking-wide text-amber-400">Turniersieger</div>
            <div className="font-display text-2xl font-bold uppercase tracking-tight text-zinc-100" data-testid="td-winner-name">{winner.name}</div>
            <div className="text-xs text-zinc-400 mt-0.5">{winner.points} Punkte · {winner.goalsFor}:{winner.goalsAgainst} Tore</div>
          </div>
        </div>
      )}

      <Tabs defaultValue="table" className="w-full">
        <TabsList className="bg-zinc-950 border border-zinc-800 grid grid-cols-3 sm:inline-grid sm:grid-cols-3 w-full sm:w-auto">
          <TabsTrigger value="table" className="data-[state=active]:bg-brand data-[state=active]:text-white uppercase tracking-wider text-xs" data-testid="tab-table">Tabelle</TabsTrigger>
          <TabsTrigger value="games" className="data-[state=active]:bg-brand data-[state=active]:text-white uppercase tracking-wider text-xs" data-testid="tab-games">Spiele</TabsTrigger>
          <TabsTrigger value="teams" className="data-[state=active]:bg-brand data-[state=active]:text-white uppercase tracking-wider text-xs" data-testid="tab-teams">Teams</TabsTrigger>
        </TabsList>

        {/* TABLE */}
        <TabsContent value="table" className="mt-4">
          <div className="border border-zinc-800 bg-zinc-900/50 overflow-x-auto" data-testid="td-table">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-[10px] uppercase tracking-wider text-zinc-400 w-10">#</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-zinc-400">{isChanging ? "Spieler" : "Team"}</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-zinc-400 text-center">Sp</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-zinc-400 text-center">S</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-zinc-400 text-center">U</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-zinc-400 text-center">N</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-zinc-400 text-center hidden sm:table-cell">Tore</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-zinc-400 text-center">Diff</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-brand-lighter text-center">Pkt</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(isChanging ? playerTable : table).map((row, i) => {
                  const color = isChanging ? null : resolveTeamColor(t.teams[row.idx]?.color, row.idx);
                  return (
                    <TableRow
                      key={isChanging ? row.id : row.idx}
                      className={"border-zinc-800 hover:bg-zinc-800/40 " + (isChanging ? "" : "cursor-pointer")}
                      onClick={isChanging ? undefined : () => setTeamModal(t.teams[row.idx])}
                      data-testid={`table-row-${i}`}
                    >
                      <TableCell className="text-zinc-300 font-display font-bold">{row.rank}</TableCell>
                      <TableCell className="text-zinc-100 font-medium" data-testid={`table-team-${i}`}>
                        <span className="flex items-center gap-2">
                          {!isChanging && (
                            <span
                              className={"inline-block h-4 w-1.5 rounded-sm shrink-0 " + color.cls}
                              data-testid={`table-team-color-${i}`}
                              title={color.label}
                            />
                          )}
                          <span className="truncate">{row.name}</span>
                        </span>
                      </TableCell>
                      <TableCell className="text-center text-zinc-300">{row.played}</TableCell>
                      <TableCell className="text-center text-zinc-300">{row.wins}</TableCell>
                      <TableCell className="text-center text-zinc-300">{row.draws}</TableCell>
                      <TableCell className="text-center text-zinc-300">{row.losses}</TableCell>
                      <TableCell className="text-center text-zinc-300 hidden sm:table-cell tabular-nums">{row.goalsFor}:{row.goalsAgainst}</TableCell>
                      <TableCell className={"text-center tabular-nums " + (row.diff > 0 ? "text-brand-lighter" : row.diff < 0 ? "text-rose-400" : "text-zinc-300")}>
                        {row.diff > 0 ? "+" : ""}{row.diff}
                      </TableCell>
                      <TableCell className="text-center font-display text-lg font-bold text-brand-lighter tabular-nums" data-testid={`table-points-${i}`}>{row.points}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* GAMES */}
        <TabsContent value="games" className="mt-4 space-y-6">
          {rounds.map((r, rIdx) => {
            const roundGames = gamesByRound[r];
            const hasRoster = !isChanging || !!(t.round_rosters && t.round_rosters[r]);
            const prevComplete = rIdx === 0 || gamesByRound[rounds[rIdx - 1]].every((g) => g.status === "beendet");
            return (
            <div key={r}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-[10px] uppercase tracking-wide text-zinc-500">
                  Spieltag {r}{roundGames[0].leg === 2 ? " · Rückrunde" : ""}
                </div>
                {isChanging && !hasRoster && !readOnly && (
                  <Button
                    onClick={() => generateRound(r)}
                    disabled={!prevComplete || drawing === r}
                    className="h-8 text-xs bg-brand hover:bg-brand-light text-white font-semibold disabled:opacity-40"
                    data-testid={`td-draw-${r}`}
                  >
                    {drawing === r ? "Lose…" : "Spieltag auslosen"}
                  </Button>
                )}
              </div>
              {isChanging && !hasRoster ? (
                <div className="border border-dashed border-zinc-800 bg-zinc-900/20 p-6 text-center text-sm text-zinc-500" data-testid={`td-round-locked-${r}`}>
                  {prevComplete
                    ? "Noch nicht ausgelost. Tippe „Spieltag auslosen“, um die Spieler auf die Teams zu verteilen."
                    : "Erst nach Abschluss aller Spiele des vorherigen Spieltags verfügbar."}
                </div>
              ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {roundGames.map((g) => {
                  const home = t.teams[g.home_idx]?.name || "?";
                  const away = t.teams[g.away_idx]?.name || "?";
                  const homeColor = resolveTeamColor(t.teams[g.home_idx]?.color, g.home_idx);
                  const awayColor = resolveTeamColor(t.teams[g.away_idx]?.color, g.away_idx);
                  return (
                    <button
                      key={g.id}
                      disabled={readOnly}
                      onClick={() => openGame(g)}
                      className={
                        "border bg-zinc-900/50 p-3 text-left transition " +
                        (readOnly
                          ? "border-zinc-800 cursor-default"
                          : "border-zinc-800 hover:border-brand-lighter/50 hover:bg-zinc-900 cursor-pointer")
                      }
                      data-testid={`game-card-${g.number}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-2">
                          <span>Spiel {g.number}</span>
                          {g.pitch_no != null && (
                            <span className="px-1.5 py-0.5 bg-zinc-800 text-brand-lighter font-semibold" data-testid={`game-pitch-${g.number}`}>
                              Feld {g.pitch_no}
                            </span>
                          )}
                        </div>
                        <div className={"text-[10px] uppercase tracking-wider px-2 py-0.5 border " + STATUS_BADGE[g.status]} data-testid={`game-status-${g.number}`}>
                          {STATUS_LABEL[g.status]}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 flex items-center gap-2 justify-end min-w-0">
                          {isChanging ? (
                            <span role="button" tabIndex={0}
                              onClick={(e) => { e.stopPropagation(); setRosterView({ round: r, slotIdx: g.home_idx }); }}
                              className="font-medium text-zinc-100 truncate underline decoration-dotted underline-offset-4 hover:text-brand-lighter cursor-pointer"
                              data-testid={`game-home-name-${g.number}`}>{home}</span>
                          ) : (
                            <div className="font-medium text-zinc-100 truncate">{home}</div>
                          )}
                          <span
                            className={"inline-block h-5 w-1.5 rounded-sm shrink-0 " + homeColor.cls}
                            data-testid={`game-color-home-${g.number}`}
                            title={homeColor.label}
                          />
                        </div>
                        <div className="font-display text-xl font-bold tabular-nums px-3 min-w-[64px] text-center">
                          {g.status === "beendet" ? (
                            <span className="text-brand-lighter" data-testid={`game-score-${g.number}`}>{g.home_goals} : {g.away_goals}</span>
                          ) : (
                            <span className="text-zinc-500">– : –</span>
                          )}
                        </div>
                        <div className="flex-1 flex items-center gap-2 min-w-0">
                          <span
                            className={"inline-block h-5 w-1.5 rounded-sm shrink-0 " + awayColor.cls}
                            data-testid={`game-color-away-${g.number}`}
                            title={awayColor.label}
                          />
                          {isChanging ? (
                            <span role="button" tabIndex={0}
                              onClick={(e) => { e.stopPropagation(); setRosterView({ round: r, slotIdx: g.away_idx }); }}
                              className="font-medium text-zinc-100 truncate underline decoration-dotted underline-offset-4 hover:text-brand-lighter cursor-pointer"
                              data-testid={`game-away-name-${g.number}`}>{away}</span>
                          ) : (
                            <div className="font-medium text-zinc-100 truncate">{away}</div>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              )}
            </div>
            );
          })}
        </TabsContent>

        {/* TEAMS */}
        <TabsContent value="teams" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {t.teams.map((team, i) => {
              const avg = teamAvgOverall(team);
              return (
                <div
                  key={i}
                  onClick={() => { if (editingTeamIdx !== i) setTeamModal(team); }}
                  className="text-left border border-zinc-800 bg-zinc-900/50 hover:border-brand-lighter/50 hover:bg-zinc-900 transition p-4 cursor-pointer rounded-md"
                  data-testid={`team-tab-card-${i}`}
                >
                  <div className="flex items-center justify-between mb-1 gap-2">
                    {editingTeamIdx === i && !readOnly ? (
                      <Input
                        autoFocus
                        value={teamNameInput}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setTeamNameInput(e.target.value)}
                        onBlur={() => saveTeamName(i)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") { e.preventDefault(); saveTeamName(i); }
                          if (e.key === "Escape") { setEditingTeamIdx(null); }
                        }}
                        className="bg-[#102A3D] border-[#1F4B6B] text-zinc-100 h-9 flex-1 min-w-0 focus-visible:ring-brand uppercase font-bold"
                        data-testid={`team-name-input-${i}`}
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          if (readOnly) return;
                          e.stopPropagation();
                          setEditingTeamIdx(i);
                          setTeamNameInput(team.name);
                        }}
                        className="flex items-center gap-1.5 min-w-0 text-left group"
                        data-testid={`team-name-edit-${i}`}
                      >
                        <span className="font-display text-lg font-bold uppercase tracking-tight text-zinc-100 truncate">{team.name}</span>
                        {!readOnly && <Pencil className="h-3.5 w-3.5 text-zinc-500 group-hover:text-brand-lighter shrink-0" />}
                      </button>
                    )}
                    <div className="font-display text-xl font-bold text-brand-lighter tabular-nums shrink-0">{avg.toFixed(1)}</div>
                  </div>
                  <div className="text-xs text-zinc-400 flex items-center gap-1">
                    <Users className="h-3 w-3" /> {team.players?.length || 0} Spieler · Ø Overall
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Result entry dialog */}
      <Dialog open={!!activeGame} onOpenChange={(o) => !o && setActiveGame(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-center text-lg" data-testid="result-dialog-title">
              Spiel {activeGame?.number} · Ergebnis
            </DialogTitle>
          </DialogHeader>
          {activeGame && (
            <div className="space-y-5 pt-2">
              <div className="grid grid-cols-2 gap-3">
                <GoalEditor
                  label={t.teams[activeGame.home_idx]?.name || "Heim"}
                  value={homeGoals}
                  onChange={setHomeGoals}
                  testIdPrefix="home"
                />
                <GoalEditor
                  label={t.teams[activeGame.away_idx]?.name || "Gast"}
                  value={awayGoals}
                  onChange={setAwayGoals}
                  testIdPrefix="away"
                />
              </div>
              {activeGame.status === "offen" && (
                <Button
                  onClick={() => setLive(activeGame)}
                  variant="outline"
                  className="w-full bg-transparent border-amber-400/40 text-amber-400 hover:bg-amber-400/10 uppercase tracking-wider font-semibold rounded-sm h-10"
                  data-testid="result-set-live-btn"
                >
                  Als laufend markieren
                </Button>
              )}
              <div className="grid grid-cols-2 gap-2">
                {activeGame.status === "beendet" && (
                  <Button
                    onClick={resetResult}
                    variant="outline"
                    className="bg-transparent border-zinc-700 text-zinc-200 hover:text-rose-400 hover:bg-zinc-800 uppercase tracking-wider font-semibold rounded-sm h-11"
                    data-testid="result-reset-btn"
                  >
                    <RotateCcw className="h-4 w-4 mr-2" /> Zurücksetzen
                  </Button>
                )}
                <Button
                  onClick={saveResult}
                  className={
                    "bg-brand hover:bg-brand-light text-white uppercase tracking-wider font-semibold rounded-sm h-11 " +
                    (activeGame.status === "beendet" ? "" : "col-span-2")
                  }
                  data-testid="result-save-btn"
                >
                  <Check className="h-4 w-4 mr-2" /> Ergebnis speichern
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Team detail dialog */}
      <Dialog open={!!teamModal} onOpenChange={(o) => !o && setTeamModal(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-xl" data-testid="team-modal-title">
              {teamModal?.name}
            </DialogTitle>
          </DialogHeader>
          {teamModal && (
            <div className="pt-2 space-y-4">
              <div className="grid grid-cols-2 gap-3 pb-3 border-b border-zinc-800">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Ø Overall</div>
                  <div className="font-display text-3xl font-bold text-brand-lighter tabular-nums">{teamAvgOverall(teamModal).toFixed(1)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Spieler</div>
                  <div className="font-display text-3xl font-bold text-zinc-100">{teamModal.players?.length || 0}</div>
                </div>
              </div>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {(teamModal.players || []).map((p) => (
                  <div key={p.id || p.name} className="flex items-center justify-between gap-2 py-1.5 border-b border-zinc-800/60 last:border-0">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-zinc-100 truncate">{p.name}</div>
                      <div className={`text-[11px] font-medium tracking-wide ${positionColor(p.position)}`}>
                        {positionAbbrev(p.position)} {p.jahrgang ? `· ${p.jahrgang}` : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="inline-flex items-center gap-0.5">
                        {Array.from({ length: 5 }).map((_, k) => (
                          <Star key={k} className={`h-3 w-3 ${k < (p.talent || 0) ? "fill-brand-lighter text-brand-lighter" : "fill-transparent text-zinc-700"}`} />
                        ))}
                      </span>
                      <span className="font-display text-base font-bold text-brand-lighter tabular-nums w-8 text-right">{(p.overall || 0).toFixed(1)}</span>
                    </div>
                  </div>
                ))}
                {(!teamModal.players || teamModal.players.length === 0) && (
                  <div className="text-xs text-zinc-500 text-center py-4">Keine Spielerdaten.</div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Spielerzuordnung eines Spieltags (nur Ansicht) */}
      <Dialog open={!!rosterView} onOpenChange={(o) => !o && setRosterView(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md">
          {rosterView && (() => {
            const slot = t.teams[rosterView.slotIdx];
            const color = resolveTeamColor(slot?.color, rosterView.slotIdx);
            const roster = (t.round_rosters?.[rosterView.round] || [])[rosterView.slotIdx] || [];
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-display text-lg font-bold tracking-tight flex items-center gap-2" data-testid="roster-view-title">
                    <span className={"inline-block h-4 w-1.5 rounded-sm " + color.cls} />
                    {slot?.name}
                    <span className="text-xs font-normal text-zinc-500">· Spieltag {rosterView.round}</span>
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-1.5 pt-1" data-testid="roster-view-list">
                  {roster.length === 0 && <div className="text-sm text-zinc-500">Keine Spieler zugeordnet.</div>}
                  {roster.map((p, pi) => (
                    <div key={p.id || pi} className="flex items-center justify-between border border-zinc-800 bg-zinc-900/50 px-3 py-2 rounded-md">
                      <span className="text-sm text-zinc-100 truncate">{p.name}</span>
                      <span className="text-xs text-zinc-500 tabular-nums">{p.position || ""}{p.overall != null ? ` · ${p.overall}` : ""}</span>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DashStat({ label, value, testId }) {
  return (
    <div className="border border-zinc-800 bg-zinc-900/50 p-3">
      <div className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="font-display text-xl font-bold text-zinc-100 tabular-nums" data-testid={testId}>{value}</div>
    </div>
  );
}

function GoalEditor({ label, value, onChange, testIdPrefix }) {
  return (
    <div className="border border-zinc-800 bg-zinc-950 p-3 flex flex-col items-center">
      <div className="text-[10px] uppercase tracking-wider text-zinc-400 text-center truncate w-full mb-2" title={label}>{label}</div>
      <div className="flex items-center gap-2 w-full justify-center">
        <button
          onClick={() => onChange(Math.max(0, value - 1))}
          className="h-12 w-12 bg-zinc-800 hover:bg-zinc-700 active:bg-zinc-600 flex items-center justify-center text-zinc-100 transition"
          data-testid={`${testIdPrefix}-minus`}
          aria-label="Minus"
        >
          <Minus className="h-5 w-5" />
        </button>
        <div className="font-display text-4xl font-bold tabular-nums w-14 text-center text-brand-lighter" data-testid={`${testIdPrefix}-goals`}>{value}</div>
        <button
          onClick={() => onChange(Math.min(99, value + 1))}
          className="h-12 w-12 bg-brand hover:bg-brand-light active:bg-brand-dark flex items-center justify-center text-white transition"
          data-testid={`${testIdPrefix}-plus`}
          aria-label="Plus"
        >
          <Plus className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
